import { Component, computed, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CommonUiProgressiveFormsModule } from '@innogy/common-ui/progressive-forms';
import {
  ORDER_CUSTOMER_TYPE_OTHER_SEGMENT,
  OrderCustomerTypeFormValues,
  showCustomerTypeExplanation,
} from '@innogy/become-a-customer/state';
import { CommonUiProductsModule } from '@innogy/common-ui/products';
import { JssModule } from '@sitecore-jss/sitecore-jss-angular';
import {
  CrntButton,
  CrntCallout,
  CrntFormControlLabel,
  CrntFormControlValidationMessage,
  CrntRadio,
  CrntRadioGroup,
} from '@essent/current-web-angular';
import { IsInvalidPipe } from '@innogy/utils-deprecated';
import { AutomationIdDirective } from '@innogy/utils-automation-id';
import { CustomerType } from '@integration/base-models';
import { Store } from '@ngrx/store';
import {
  GenericRichTextDirective,
  TextInterpolationPipe,
} from '@core/jss-utils';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import { SetValueAction } from 'ngrx-forms';

import { OrderFulfillmentStepComponent } from '../order-fulfillment-step.component';

@Component({
  selector: 'wl-order-fulfillment-customer-type[progressiveFormState]',
  templateUrl: './order-fulfillment-customer-type.component.html',
  styleUrls: [
    './order-fulfillment-customer-type.component.essent.scss',
    'order-fulfillment-customer-type.component.ed.scss',
  ],
  standalone: true,
  imports: [
    CommonModule,
    CommonUiProgressiveFormsModule,
    CommonUiProductsModule,
    JssModule,
    CrntRadioGroup,
    CrntFormControlLabel,
    CrntFormControlValidationMessage,
    IsInvalidPipe,
    CrntRadio,
    AutomationIdDirective,
    TextInterpolationPipe,
    GenericRichTextDirective,
    CrntCallout,
    CrntButton,
  ],
})
export class OrderFulfillmentCustomerTypeComponent
  extends OrderFulfillmentStepComponent<OrderCustomerTypeFormValues>
  implements OnInit
{
  private readonly store$ = inject(Store);
  private readonly envConfig = inject(ENVIRONMENT_CONFIG);
  public isSme = false;

  ngOnInit() {
    const isSme = this.envConfig.segment === 'zakelijk';
    this.isSme = isSme;

    // in case of SME, we only show the companySize question, so set a default for the segmentQuestion to retain the same logic
    if (isSme) {
      this.store$.dispatch(
        new SetValueAction(
          this.formState().controls.segmentQuestion.id,
          ORDER_CUSTOMER_TYPE_OTHER_SEGMENT
        )
      );
    }
  }

  override readonly progressiveFormCardContent = computed(() => {
    return {
      title: this.orderContent().customerTypeCardTitle,
      subtitle: this.orderContent().customerTypeCardSubtitle,
      submitButtonText: this.orderContent().customerTypeSubmitButtonText,
      stepNumber: this.stepNumber(),
    };
  });

  public readonly customerType = computed(() => {
    if (
      !!this.formState().value.segmentQuestion &&
      this.formState().value.segmentQuestion !== CustomerType.HOUSEHOLD
    ) {
      return this.formState().value.companySize;
    } else {
      return this.formState().value.segmentQuestion;
    }
  });

  public readonly readableCustomerType = computed(() => {
    let text: string | null = null;
    if (this.customerType()) {
      text =
        this.orderContent()
          .customerTypeSummaryOptions.find(
            (opt) => opt.fields['value']?.value === this.customerType()
          )
          ?.fields['label']?.value.toString() ?? null;
    }
    return text;
  });

  openModal() {
    this.store$.dispatch(
      showCustomerTypeExplanation({ payload: this.orderContent() })
    );
  }

  // map the sitecore value to our enum, because we don't want to rely on sitecore values in the app
  mapSitecoreValueToCustomerType(
    value: string
  ): CustomerType | typeof ORDER_CUSTOMER_TYPE_OTHER_SEGMENT | undefined {
    switch (value) {
      case 'household':
        return CustomerType.HOUSEHOLD;
      case 'micro':
        return CustomerType.MICRO;
      case 'small':
        return CustomerType.SMALL;
      case 'medium':
        return CustomerType.MEDIUM;
      case 'large':
        return CustomerType.LARGE;
      case 'sohoOrSme':
        return ORDER_CUSTOMER_TYPE_OTHER_SEGMENT;
      default:
        return undefined;
    }
  }

  protected readonly ORDER_CUSTOMER_TYPE_OTHER_SEGMENT =
    ORDER_CUSTOMER_TYPE_OTHER_SEGMENT;
}
